import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HealingIcon from '@mui/icons-material/Healing'; // For recovery/benefits
import ReportProblemIcon from '@mui/icons-material/ReportProblem'; // For when necessary
import InfoIcon from '@mui/icons-material/Info'; // For types/info
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload'; // For process
import SpaIcon from '@mui/icons-material/Spa'; // For comfort / why choose us
import StarIcon from '@mui/icons-material/Star'; // For why choose us
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'; // For goals
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'; // For aftercare steps
import DeviceHubIcon from '@mui/icons-material/DeviceHub'; // For replacement options (like a hub for new teeth)
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const ToothExtractions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const goalsOfExtraction = [
    'Alleviates severe tooth pain or discomfort.',
    'Removes the source of infection, preventing its spread.',
    'Addresses problematic wisdom teeth causing pain or crowding.',
    'Creates necessary space for orthodontic treatment.',
    'Removes teeth too damaged or decayed to be saved.',
    'Prepares the mouth for restorative solutions like dentures or implants.'
  ];

  const process = [
    {
      title: '1. Consultation, Examination & X-Rays',
      description: 'We begin with a comprehensive exam, including digital X-rays, to assess the tooth and surrounding bone. We discuss your medical history and the reasons for extraction.'
    },
    {
      title: '2. Anesthesia & Ensuring Your Comfort',
      description: 'Your comfort is our priority. Local anesthesia will be administered to completely numb the area. We also offer sedation options (if applicable at your practice) for anxious patients.'
    },
    {
      title: '3. The Extraction Procedure',
      description: 'For a simple extraction, the tooth is gently loosened and removed. For surgical extractions (e.g., impacted wisdom teeth), a small incision may be needed to access and remove the tooth. You will feel pressure, but not pain.'
    },
    {
      title: '4. Post-Extraction Care & Instructions',
      description: 'After the extraction, we will place sterile gauze over the site to control bleeding and provide you with detailed aftercare instructions to ensure a smooth healing process.'
    }
  ];

  const whenNecessary = [
    'A tooth is severely decayed or damaged beyond repair.',
    'Advanced periodontal (gum) disease has loosened the tooth.',
    'An infection or abscess is present that cannot be resolved by other treatments.',
    'Impacted wisdom teeth are causing pain, infection, or damage to adjacent teeth.',
    'Orthodontic treatment requires space for proper teeth alignment.',
    'A tooth is fractured in a way that makes restoration impossible.'
  ];

  const typesOfExtractions = [
    {
      name: 'Simple Extractions',
      desc: 'Performed on teeth that are visible in the mouth and can be removed relatively easily. The tooth is gently loosened and lifted from its socket.',
      icon: <InfoIcon sx={{ color: 'primary.main' }}/>
    },
    {
      name: 'Surgical Extractions',
      desc: 'Required for teeth that are not easily accessible, such as those broken off at the gum line or impacted (e.g., wisdom teeth). This may involve making a small incision in the gum.',
      icon: <AssuredWorkloadIcon sx={{ color: 'secondary.main' }}/> // Using this for 'more involved'
    },
    {
      name: 'Wisdom Tooth Extractions',
      desc: 'A common type of surgical extraction to remove third molars, which often lack space to erupt properly, leading to pain, infection, or other dental problems.',
      icon: <ReportProblemIcon sx={{ color: 'error.main' }}/> // As wisdom teeth are often 'problems'
    },
  ];

  const aftercareTips = [
    'Bite gently on the provided gauze pad for 30-60 minutes to control bleeding; change as needed.',
    'Apply ice packs to the outside of your cheek for 15-20 minutes at a time for the first 24-48 hours to reduce swelling.',
    'Take pain medication as prescribed or recommended by your dentist.',
    'Rest for the remainder of the day; avoid strenuous activity for a few days.',
    'Stick to a soft food diet for the first few days (e.g., yogurt, mashed potatoes, smoothies - no straws!).',
    'After 24 hours, begin gentle warm salt water rinses (1/2 teaspoon salt in 8oz water) 2-3 times a day, especially after meals.',
    'Do NOT smoke, spit forcefully, or drink through a straw for at least 48-72 hours to prevent dislodging the blood clot (dry socket).',
    'Brush and floss your other teeth gently, avoiding the extraction site initially.',
    'Attend any scheduled follow-up appointments.'
  ];
  
  const replacementOptions = [
    {
      name: 'Dental Implants',
      desc: 'A durable, natural-looking option that replaces the tooth root and crown, preserving bone health.',
      icon: <DeviceHubIcon sx={{color: 'primary.main'}} /> // Represents a foundational 'hub'
    },
    {
      name: 'Dental Bridges',
      desc: 'Utilizes adjacent teeth to support a prosthetic tooth, "bridging" the gap.',
      icon: <DeviceHubIcon sx={{color: 'primary.light'}} />
    },
    {
      name: 'Partial or Full Dentures',
      desc: 'Removable appliances that can replace one or more missing teeth (partial) or all teeth (full).',
      icon: <DeviceHubIcon sx={{color: 'secondary.main'}} />
    }
  ]

  const faqs = [
    {
      question: 'Is a tooth extraction painful?',
      answer: 'During the procedure, you should not feel pain due to the local anesthetic. You will feel pressure. After the anesthesia wears off, some discomfort and tenderness are normal, which can be managed with pain medication.'
    },
    {
      question: 'How long is the recovery after a tooth extraction?',
      answer: 'Initial healing typically takes 1-2 weeks. Swelling and discomfort usually peak within the first 2-3 days and then gradually subside. Complete healing of the bone can take several months.'
    },
    {
      question: 'What is a dry socket and how can I prevent it?',
      answer: 'A dry socket occurs when the blood clot in the extraction site is dislodged or dissolves prematurely, exposing bone and nerves, which can be very painful. To prevent it, avoid smoking, spitting, using straws, and follow all aftercare instructions carefully.'
    },
    {
        question: 'When can I eat normally after an extraction?',
        answer: 'Stick to soft foods for the first few days. You can gradually reintroduce firmer foods as your comfort allows, but avoid chewing directly on the extraction site until it has healed significantly.'
    },
    {
        question: 'Will I need stitches?',
        answer: 'Some extractions, especially surgical ones, may require stitches. These are often dissolvable and will disappear on their own within a week or two. Your dentist will inform you if stitches are used.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Tooth Extractions | Gentle Tooth Removal | Pacifica Dental</title>
        <meta name="description" content="Gentle and comfortable tooth extractions at Pacifica Dental. Learn about when extractions are necessary, the process, aftercare, and wisdom teeth removal." />
        <meta name="keywords" content="tooth extraction, dental extraction, tooth removal, wisdom teeth removal, surgical extraction, oral surgery, [Your City] dentist, gentle dentist" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Gentle Tooth Extractions: Prioritizing Your Comfort
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              Safe and Effective Tooth Removal When Necessary
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              At Pacifica Dental, we understand that the thought of a tooth extraction can be concerning. Rest assured, our experienced team is committed to making your procedure as comfortable and stress-free as possible. While our primary goal is always to save natural teeth, an extraction is sometimes the best course of action to protect your overall oral health, relieve pain, or prepare for further restorative treatments. We utilize modern techniques and provide clear guidance every step of the way.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Goals of Extraction Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Primary Goals of Tooth Extraction
                  </Typography>
                  <List dense>
                    {goalsOfExtraction.map((goal, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                          <HealthAndSafetyIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                        </ListItemIcon>
                        <ListItemText primary={goal} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Process Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    The Tooth Extraction Journey
                  </Typography>
                  {process.map((step, index) => (
                    <Box key={index} sx={{ mb: index === process.length - 1 ? 0 : 2.5 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                         <AssuredWorkloadIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
                        <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: '2.1rem'}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* When is an Extraction Necessary? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                When is a Tooth Extraction Unavoidable?
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#fff3e0' : '#4e342e' }}>
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  An extraction may be recommended in situations where a tooth cannot be saved or poses a risk to your oral health, such as:
                </Typography>
                <Grid container spacing={1}>
                    {whenNecessary.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'error.main'}}>
                                        <ReportProblemIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Types of Extractions Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Types of Extractions We Perform
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                    {typesOfExtractions.map((type, index) => (
                        <Grid item xs={12} sm={6} md={isMobile ? 12 : (typesOfExtractions.length === 3 ? 4 : 3)} key={index}>
                            <Paper elevation={2} sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                                <Box sx={{display: 'flex', alignItems: 'center', mb:1}}>
                                    {type.icon}
                                    <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize:'1.1rem', color: 'secondary.dark', ml:1}}>
                                        {type.name}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{flexGrow: 1, fontSize: '0.95rem'}}>
                                    {type.desc}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Aftercare Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                    Essential Aftercare for a Smooth Recovery
                </Typography>
                <Typography variant="subtitle1" textAlign="center" color="text.secondary" sx={{mb:3}}>
                    Following these instructions carefully is crucial for comfortable healing and preventing complications.
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}>
                    <List>
                        {aftercareTips.map((tip, index) => (
                            <ListItem key={index} sx={{ alignItems: 'flex-start' }}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark', mt: 0.5}}>
                                    <PlaylistAddCheckIcon />
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />

            {/* Tooth Replacement Options Section */}
             <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                    Considering Your Smile's Future: Tooth Replacement
                </Typography>
                 <Typography variant="subtitle1" textAlign="center" color="text.secondary" sx={{mb:3}}>
                    After an extraction (excluding wisdom teeth not part of your bite), it's important to discuss replacing the missing tooth to maintain your oral health and function.
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                    {replacementOptions.map((option, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper elevation={2} sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, textAlign:'center' }}>
                                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', mb:1.5}}>
                                    {option.icon}
                                    <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize:'1.15rem', color: 'secondary.dark', mt:1}}>
                                        {option.name}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{flexGrow: 1, fontSize: '0.95rem'}}>
                                    {option.desc}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />


            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Common Questions About Tooth Extractions
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Us Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Choose Us for Your Tooth Extraction?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Experienced & Gentle Approach", text: "Our skilled dentists perform extractions with utmost care, prioritizing your comfort and minimizing discomfort.", icon: <SpaIcon color="primary" /> },
                      { title: "Clear Communication & Options", text: "We thoroughly explain the procedure, answer your questions, and discuss all options, including tooth replacement.", icon: <CheckCircleOutlineIcon color="primary" /> },
                      { title: "Comprehensive Aftercare Support", text: "You'll receive detailed instructions and support for a smooth recovery, aimed at preventing complications.", icon: <HealingIcon color="primary" /> },
                      { title: "Focus on Your Overall Oral Health", text: "We consider the long-term impact and work with you to ensure your smile remains healthy and functional.", icon: <StarIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Need to Discuss a Problematic Tooth?
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    If you're experiencing dental pain or have been advised you may need an extraction, contact us for a consultation.
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Extraction Consultation
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                For urgent concerns, please call us at <Link to="tel:(408) 869-0998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link>.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default ToothExtractions;